import React, {useEffect, useRef, useState} from "react";
import {PlayGameButton} from "games/GameOffer";
import {GameDialog} from "games/GameDialog";
import {p2pGameProgress, runHasFinish} from "games/gameProgress";
import {useTransferGame} from "games/useTransferGame";
import {useMessages} from "i18n";
import {PeerActivityListener, Unsubscribe} from "core/p2p/peerActivity";
import {useBeforeUnloadWarning} from "home/useBeforeUnloadWarning";
import {liveCardNote, liveCardState, liveCardTitle, showsLiveLink, warnsBeforeLeaving} from "./instantStatus";
import {KeepTabOpenDialog} from "./KeepTabOpenDialog";
import {LiveRecipients} from "./LiveRecipients";
import {useKeepTabOpenNotice} from "./useKeepTabOpenNotice";
import {usePeerActivity} from "./usePeerActivity";
import {ShareActionsRow} from "./ShareActionsRow";
import {ShareLinkBox} from "./ShareLinkBox";
import {
	GhostSubmit, Head, LiveCard, LiveDot, LiveSub, SeedingErrorNote, StatusTitle, TerminatedNote,
} from "./InstantLiveCard.styles";

/**
 * The instant/P2P success screen (ticket #20), as cleaned up in kitecorp/transferify-ui#3320: one title and one
 * sentence saying the link lives as long as this tab, the link box with "Copy" on show, the share row ("Show QR
 * code", and the game's Play control when this transfer offers one, both opening a dialog), the late
 * personalization offer, and the recipients: a ring over everyone and a row per recipient. Then "Send another".
 * While the signaling socket reconnects, the title says so and the note under it says nobody new can reach the
 * sender yet (kitecorp/transferify-ui#3383).
 *
 * The moment the card first shows the link, the keep-this-tab-open notice opens over it, unless this browser asked
 * not to see it again, and the browser asks before the tab closes or reloads for as long as the link is live
 * (kitecorp/transferify-ui#3724).
 *
 * The share row and the link box are the hosted success card's own (ui#2486, kitecorp/transferify-ui#3333), so the
 * two cards share one arrangement and one set of primitives.
 *
 * Live state comes exclusively through `subscribeActivity` (TransferService.onPeerActivity) — this component
 * never touches the web-peer stack (SSR-stubbed, see `core/p2p/peerActivity`). The rules it renders are in
 * `./instantStatus` and `./liveRecipientRules`.
 */

interface Props {
	url: string;
	/**
	 * The late personalization offer, rendered under the share row (ui#2374).
	 *
	 * An instant transfer has a download page like any other, and since api#1592 it can take a look
	 * composed after the send — so this card carries the same entry the hosted one does. It is placed
	 * inside the live-link branch on purpose: a terminated transfer's link is dead, and offering to
	 * decorate it under the notice saying so would be an action with nothing behind it.
	 */
	personaliseAction?: React.ReactNode;
	onSendAnother: () => void;
	subscribeActivity: (listener: PeerActivityListener) => Unsubscribe;
}

export function InstantLiveCard({url, personaliseAction, onSendAnother, subscribeActivity}: Props) {
	const {sender} = useMessages();
	const activity = usePeerActivity(subscribeActivity);
	const live = showsLiveLink(activity);
	const note = liveCardNote(activity, sender.instant);
	// The file is served from this tab, so closing it while the link is live asks first (kitecorp/transferify-ui#3724).
	useBeforeUnloadWarning(warnsBeforeLeaving(activity));
	const keepTabOpen = useKeepTabOpenNotice(live);
	// Where focus goes back to once the notice has closed: the card's title, the first thing on the card.
	const title = useRef<HTMLHeadingElement>(null);
	/*
	 * Byte Runner (kitecorp/transferify-ui#2872): offered from the start of the live card, because no
	 * recipient means no ETA to judge by, and available for as long as the link is live. Its finish line
	 * is the FIRST recipient's download. A run started after that download completed has no finish.
	 */
	const game = useTransferGame("p2p", live, url);
	const gameProgress = p2pGameProgress(activity.firstRecipient);
	const [endlessRun, setEndlessRun] = useState(false);
	const {transferCompleted, close: closeGame} = game;
	useEffect(() => {
		if (gameProgress.done) transferCompleted();
	}, [gameProgress.done, transferCompleted]);
	useEffect(() => {
		if (!live) closeGame();
	}, [live, closeGame]);
	const play = () => {
		setEndlessRun(!runHasFinish(gameProgress));
		game.play();
	};

	return (
		<LiveCard>
			<Head>
				<StatusTitle ref={title} tabIndex={-1}>
					<LiveDot $state={liveCardState(activity)} aria-hidden="true"/>
					{liveCardTitle(activity, sender.instant)}
				</StatusTitle>
				{/* A status region, so a screen reader hears the note change when signaling drops and comes back. */}
				{note !== null && <LiveSub role="status">{note}</LiveSub>}
			</Head>
			{activity.seedingError !== null && (
				<SeedingErrorNote role="alert">
					{sender.instant.fileUnreadable(activity.seedingError)}
				</SeedingErrorNote>
			)}
			{live ? (
				<>
					<ShareLinkBox url={url} persistentCopy/>
					<ShareActionsRow url={url} play={game.offersPlay && game.game !== null
						? <PlayGameButton game={game.game} onPlay={play} compact/>
						: null}/>
					{personaliseAction}
					<LiveRecipients recipients={activity.recipients}/>
					{game.game !== null && (
						<GameDialog
							open={game.open}
							game={game.game}
							mode="p2p"
							progress={gameProgress}
							endless={endlessRun}
							onClose={game.close}
							onContinue={game.close}
							onRunFinished={game.runFinished}
							onRunClosed={game.runClosed}
						/>
					)}
				</>
			) : (
				<TerminatedNote role="alert">{sender.instant.terminatedNotice}</TerminatedNote>
			)}
			<GhostSubmit type="button" onClick={onSendAnother}>{sender.sendAnother}</GhostSubmit>
			<KeepTabOpenDialog
				open={keepTabOpen.open}
				dontShowAgain={keepTabOpen.dontShowAgain}
				onDontShowAgainChange={keepTabOpen.setDontShowAgain}
				onClose={keepTabOpen.close}
				onExited={() => title.current?.focus()}
			/>
		</LiveCard>
	);
}
